import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
    FiUploadCloud, FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiCopy, FiCheck,
    FiFileText, FiX, FiClock, FiRefreshCw, FiInfo, FiCalendar,
} from 'react-icons/fi';
import { FinCheck } from '../../components/FinField';
import '../../css/financeiro-parcela-pagar.css';
import './UploadNotaFiscalLink.css';
import {
    marcadasIniciais, alternarMarcada, valorMarcadas, idsParaEnviar, rotuloParcela, avisoParcelas,
} from './nfParcelasPolicy';

/**
 * Link público do fornecedor para enviar a NF (Rafael 13/10): mobile-first, mostra exatamente o que a
 * nota precisa ter (tomador, CNPJ, endereço, valor, tipo, serviços com valor) e, se a conferência
 * automática apontar inconsistência, a nota NÃO é aceita — o fornecedor vê o motivo e reenvia por
 * este mesmo link.
 * 21/10: orientações para emissão (doc "Orientações para Emissão da Nota Fiscal"): o que vai na descrição da
 * nota já preenchido, conta no mesmo CNPJ, fechamento contábil (dia 25 ao fim do mês, só aviso) e pagamento às
 * sextas (o produtor só pelo nome, sem e-mail/telefone da equipe); a tela virou um passo a passo 1 · Confira, 2 · Descrição, 3 · Envie.
 */
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost';

const brl = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const dataHora = (s) => {
    if (!s) return '';
    const d = new Date(String(s).replace(' ', 'T'));
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR') + ' às ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const soData = (s) => {
    if (!s) return '';
    const d = new Date(String(s).replace(' ', 'T'));
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

const tamanho = (bytes) => (bytes < 1024 * 1024
    ? Math.max(1, Math.round(bytes / 1024)) + ' KB'
    : (bytes / 1024 / 1024).toFixed(1).replace('.', ',') + ' MB');

const nomeCurto = (nome, max = 42) => {
    if (!nome || nome.length <= max) return nome;
    const ext = nome.includes('.') ? nome.slice(nome.lastIndexOf('.')) : '';
    return nome.slice(0, max - ext.length - 3) + '...' + ext;
};

const MAX_ARQUIVOS = 6;

const validadeTexto = (dados) => {
    const d = soData(dados?.data_expiracao);
    return d ? `, até ${d}` : '';
};

const ESTADOS_LINK = {
    invalido:   { titulo: 'Link inválido', texto: 'Este link não existe ou foi digitado errado. Confira o link no e-mail que você recebeu.' },
    expirado:   { titulo: 'Link expirado', texto: 'O prazo deste link acabou. Fale com a equipe de eventos da Access para receber um link novo.' },
    processado: { titulo: 'Nota já recebida', texto: 'Esta solicitação já recebeu as notas fiscais pedidas. Se precisar trocar uma nota, fale com a equipe de eventos da Access.' },
};

function Copiar({ valor }) {
    const [ok, setOk] = useState(false);
    if (!valor) return null;
    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(String(valor));
            setOk(true);
            setTimeout(() => setOk(false), 1600);
        } catch (e) {
            // Sem permissão de clipboard: o texto continua selecionável na tela.
        }
    };
    return (
        <button type="button" className={`nfl__copy ${ok ? 'is-ok' : ''}`} onClick={copiar} aria-label="Copiar">
            {ok ? <><FiCheck size={14} /> Copiado</> : <><FiCopy size={14} /> Copiar</>}
        </button>
    );
}

function Cabecalho({ evento }) {
    return (
        <header className="nfl__top">
            <img
                src="/images/access-financeiro-logo.png"
                alt="Access Financeiro"
                className="nfl__logo"
                onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="nfl__top-title">Envio de nota fiscal</h1>
            {evento ? <p className="nfl__top-sub">{evento}</p> : null}
        </header>
    );
}

// Turma 5 (20/10): o que a Access pediu para constar (voucher, hóspede, período) ou o motivo do reenvio.
function ObservacoesAccess({ texto }) {
    if (!texto) return null;
    return (
        <section className="nfl__obs" data-observacao-solicitante>
            <div className="nfl__obs-head"><FiFileText size={18} /> Observações da Access para esta nota</div>
            <p className="nfl__obs-text">{texto}</p>
        </section>
    );
}

// D-0099: parcelas do pedido. O fornecedor marca as que esta nota cobre (padrão: uma nota por parcela);
// as que já receberam nota aparecem como recebidas e não podem ser marcadas.
function ParcelasDaNota({ parcelas, marcadas, onAlternar, travado }) {
    const varias = parcelas.filter((p) => !p.tem_nf).length > 1;
    return (
        <div className="nfl__parcelas" data-parcelas-nf>
            <h3 className="nfl__h3">{parcelas.length > 1 ? 'Parcelas desta solicitação' : 'Parcela desta solicitação'}</h3>
            {varias ? (
                <p className="nfl__hint">
                    Marque a parcela que esta nota cobre. Uma nota pode cobrir mais de uma parcela: o valor dela é a soma das parcelas marcadas.
                </p>
            ) : null}
            <table className="nfl__tbl">
                <thead>
                    <tr><th>Parcela</th><th>Pagamento previsto</th><th className="nfl__tbl-num">Valor</th></tr>
                </thead>
                <tbody>
                    {parcelas.map((p) => (
                        <tr key={p.id} className={p.tem_nf ? 'is-recebida' : ''}>
                            <td>
                                {p.tem_nf ? (
                                    <span className="nfl__parcela-ok">
                                        {rotuloParcela(p)}
                                        <span className="fin-pp__pill is-ok"><FiCheck size={12} /> Nota recebida</span>
                                    </span>
                                ) : (
                                    <FinCheck
                                        id={`nfl-parcela-${p.id}`}
                                        label={rotuloParcela(p)}
                                        checked={marcadas.includes(String(p.id))}
                                        disabled={travado || !varias}
                                        onChange={() => onAlternar(p.id)}
                                    />
                                )}
                            </td>
                            <td className="nfl__muted">{p.vencimento || '—'}</td>
                            <td className="nfl__tbl-num">{p.valor_fmt || brl(p.valor)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Instrucoes({ i, fallback, semEvento, parcelas = [], marcadas = [], onAlternar, travado }) {
    if (!i) {
        return (
            <section className="nfl__card">
                <h2 className="nfl__h2"><FiFileText size={18} /> Dados da solicitação</h2>
                <dl className="nfl__dl">
                    <div className="nfl__row"><dt>Fornecedor</dt><dd>{fallback?.fornecedor_nome}</dd></div>
                    <div className="nfl__row"><dt>Valor</dt><dd className="nfl__num">{brl(fallback?.valor_total)}</dd></div>
                </dl>
            </section>
        );
    }
    const t = i.tomador || {};
    const ev = i.evento || {};
    // Por parcela: o valor da nota é a soma das parcelas marcadas, e o valor por serviço (do orçamento
    // inteiro) sai para não ser confundido com o da nota.
    const porParcela = parcelas.length > 0;
    const valorNota = porParcela ? valorMarcadas(parcelas, marcadas) : null;
    const valorFmt = porParcela ? (valorNota > 0 ? brl(valorNota) : '—') : (i.valor_fmt || brl(i.valor));
    return (
        <section className="nfl__card">
            <h2 className="nfl__h2"><span className="nfl__step">1</span> Dados que devem constar na nota</h2>
            <p className="nfl__hint">A nota é conferida automaticamente. Se algum destes dados estiver diferente, ela não é aceita.</p>

            {porParcela ? (
                <ParcelasDaNota parcelas={parcelas} marcadas={marcadas} onAlternar={onAlternar} travado={travado} />
            ) : null}

            <div className="nfl__valor">
                <span className="nfl__valor-label">{porParcela ? 'Valor exato desta nota' : 'Valor exato da nota'}</span>
                <span className="nfl__valor-num">{valorFmt}</span>
                <Copiar valor={valorFmt !== '—' ? valorFmt.replace('R$ ', '') : ''} />
            </div>

            <dl className="nfl__dl">
                {t.razao_social ? (
                    <div className="nfl__row"><dt>Emitir para (tomador)</dt><dd>{t.razao_social}</dd></div>
                ) : null}
                {t.cnpj ? (
                    <div className="nfl__row">
                        <dt>CNPJ do tomador</dt>
                        <dd className="nfl__dd-copy"><span className="nfl__num">{t.cnpj}</span><Copiar valor={t.cnpj} /></dd>
                    </div>
                ) : null}
                {t.endereco ? (
                    <div className="nfl__row"><dt>Endereço do tomador</dt><dd>{t.endereco}</dd></div>
                ) : null}
                <div className="nfl__row"><dt>Tipo de documento</dt><dd>{i.tipo_rotulo}</dd></div>
                {i.fornecedor?.nome ? (
                    <div className="nfl__row">
                        <dt>Emitente (sua empresa)</dt>
                        <dd>{i.fornecedor.nome}{i.fornecedor.cnpj ? <span className="nfl__muted"> · CNPJ {i.fornecedor.cnpj}</span> : null}</dd>
                    </div>
                ) : null}
                {ev.nome && !semEvento ? (
                    <div className="nfl__row"><dt>Evento</dt><dd>{ev.nome}{ev.periodo ? <span className="nfl__muted"> · {ev.periodo}</span> : null}</dd></div>
                ) : null}
                {i.orcamento ? (
                    <div className="nfl__row"><dt>Orçamento</dt><dd className="nfl__num">#{i.orcamento}</dd></div>
                ) : null}
            </dl>

            {(i.itens || []).length > 0 ? (
                <div className="nfl__itens">
                    <h3 className="nfl__h3">Serviços desta nota</h3>
                    <table className="nfl__tbl">
                        <thead><tr><th>Serviço</th>{porParcela ? null : <th className="nfl__tbl-num">Valor</th>}</tr></thead>
                        <tbody>
                            {i.itens.map((it, k) => (
                                <tr key={k}>
                                    <td>{it.descricao}</td>
                                    {porParcela ? null : <td className="nfl__tbl-num">{Number(it.valor) > 0 ? brl(it.valor) : '—'}</td>}
                                </tr>
                            ))}
                        </tbody>
                        {porParcela ? null : (
                            <tfoot><tr><td>Total da nota</td><td className="nfl__tbl-num">{i.valor_fmt || brl(i.valor)}</td></tr></tfoot>
                        )}
                    </table>
                </div>
            ) : null}
        </section>
    );
}

// Texto pronto para colar na descrição da nota: só os campos que já vêm preenchidos.
export function textoDescricaoNota(orientacoes) {
    return ((orientacoes && orientacoes.descricao) || [])
        .filter((d) => d.valor)
        .map((d) => `${d.label}: ${d.valor}`)
        .join('\n');
}

function Passos() {
    return (
        <ol className="nfl__passos" aria-label="Como enviar">
            <li><span className="nfl__step">1</span> Confira</li>
            <li><span className="nfl__step">2</span> Emita</li>
            <li><span className="nfl__step">3</span> Envie</li>
        </ol>
    );
}

function AvisoFechamento({ orientacoes }) {
    if (!orientacoes || !orientacoes.fechamento_contabil) return null;
    return (
        <section className="nfl__aviso" role="alert" data-fechamento-contabil>
            <div className="nfl__aviso-head"><FiCalendar size={18} /> Período de fechamento contábil</div>
            <p className="nfl__aviso-text">
                Do dia 25 ao fim do mês a Access não recebe notas fiscais. Emita e envie a nota a partir do dia 1º do mês seguinte.
            </p>
        </section>
    );
}

function Orientacoes({ o }) {
    if (!o) return null;
    const texto = textoDescricaoNota(o);
    return (
        <section className="nfl__card" data-orientacoes>
            <h2 className="nfl__h2"><span className="nfl__step">2</span> Na descrição da nota, informe</h2>
            <ul className="nfl__ori">
                {(o.descricao || []).map((d, k) => (
                    <li key={k} className="nfl__ori-item">
                        <span className="nfl__ori-label">{d.label}</span>
                        {d.valor ? <span className="nfl__ori-valor">{d.valor}</span> : null}
                        {d.dica ? <span className="nfl__ori-dica">{d.dica}</span> : null}
                    </li>
                ))}
            </ul>
            {texto ? (
                <div className="nfl__ori-copiar">
                    <span className="nfl__muted">Copie evento, período e serviço para colar na nota</span>
                    <Copiar valor={texto} />
                </div>
            ) : null}

            {(o.importantes || []).length > 0 ? (
                <div className="nfl__itens">
                    <h3 className="nfl__h3">Informações importantes</h3>
                    <ul className="nfl__imp">
                        {o.importantes.map((t, k) => <li key={k}><FiInfo size={16} /> <span>{t}</span></li>)}
                    </ul>
                </div>
            ) : null}

        </section>
    );
}

function ListaMotivos({ itens }) {
    if (!itens || itens.length === 0) return null;
    return (
        <ul className="nfl__motivos">
            {itens.map((it, k) => <li key={k}>{it.mensagem}</li>)}
        </ul>
    );
}

export default function UploadNotaFiscal() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [estadoLink, setEstadoLink] = useState(null); // {codigo, mensagem}
    const [dados, setDados] = useState(null);
    // Vários arquivos (13/10): exatamente 1 "válida" (passa pela conferência) + as canceladas na prefeitura
    // (comprovante para o Financeiro). Cada item: {id, file, tipo: 'valida' | 'cancelada'}.
    const [arquivos, setArquivos] = useState([]);
    const [erro, setErro] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [resultado, setResultado] = useState(null); // {tipo:'ok'|'rejeitada', validacao, instrucoes}
    // D-0099: ids (texto) das parcelas que esta nota cobre.
    const [marcadas, setMarcadas] = useState([]);
    const inputRef = useRef(null);

    useEffect(() => {
        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const carregar = async () => {
        try {
            setLoading(true);
            const r = await axios.get(`${API_URL}/public/nf-upload/${token}`);
            if (r.data.sucesso) {
                setDados(r.data.dados);
                // Nota corrigida: mantém as parcelas marcadas que ainda esperam nota; senão, o padrão.
                setMarcadas((atual) => {
                    const ainda = idsParaEnviar(r.data.dados?.parcelas, atual);
                    return ainda.length ? ainda : marcadasIniciais(r.data.dados?.parcelas);
                });
                setEstadoLink(null);
            } else {
                setEstadoLink({ codigo: r.data.codigo || 'invalido', mensagem: r.data.mensagem });
            }
        } catch (e) {
            const d = e.response?.data;
            if (d?.codigo) setEstadoLink({ codigo: d.codigo, mensagem: d.mensagem });
            else setEstadoLink({ codigo: 'falha', mensagem: d?.mensagem || 'Não foi possível carregar. Verifique sua conexão e tente de novo.' });
        } finally {
            setLoading(false);
        }
    };

    const escolher = (file) => {
        if (!file) return;
        if (arquivos.length >= MAX_ARQUIVOS) {
            setErro(`Envie no máximo ${MAX_ARQUIVOS} arquivos (1 nota válida e até ${MAX_ARQUIVOS - 1} canceladas).`);
            return;
        }
        const tipos = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'text/xml', 'application/xml'];
        // XML: o tipo varia por navegador/SO (text/xml, application/xml ou vazio) → olha a extensão.
        const ehXml = /\.xml$/i.test(file.name || '');
        if (!tipos.includes(file.type) && !ehXml) {
            setErro('Tipo de arquivo não aceito. Envie PDF, XML, JPG ou PNG.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setErro('Arquivo muito grande. O máximo é 10 MB.');
            return;
        }
        // 1º arquivo = a nota válida; os seguintes entram como cancelados (o fornecedor troca se precisar).
        setArquivos((lista) => [...lista, {
            id: `${Date.now()}-${lista.length}`,
            file,
            tipo: lista.some((a) => a.tipo === 'valida') ? 'cancelada' : 'valida',
        }]);
        setErro(null);
    };

    const marcar = (id, tipo) => {
        setArquivos((lista) => lista.map((a) => {
            if (a.id === id) return { ...a, tipo };
            // Só uma válida: marcar outra como válida passa a anterior para cancelada.
            return tipo === 'valida' && a.tipo === 'valida' ? { ...a, tipo: 'cancelada' } : a;
        }));
    };

    const remover = (id) => setArquivos((lista) => lista.filter((a) => a.id !== id));

    const parcelas = dados?.parcelas || [];
    const parcelasEnvio = idsParaEnviar(parcelas, marcadas);
    const avisoParcela = avisoParcelas(parcelas, marcadas);
    const validas = arquivos.filter((a) => a.tipo === 'valida');
    const avisoEnvio = avisoParcela
        || (arquivos.length === 0 ? null
            : validas.length === 0 ? 'Marque qual arquivo é a nota válida (a que deve ser paga).'
            : null);
    const podeEnviar = arquivos.length > 0 && validas.length === 1 && !avisoParcela && !enviando;

    const enviar = async () => {
        if (!podeEnviar) return;
        try {
            setEnviando(true);
            setErro(null);
            const fd = new FormData();
            fd.append('arquivo_nf', validas[0].file);
            arquivos.filter((a) => a.tipo === 'cancelada').forEach((a) => fd.append('nf_cancelada[]', a.file));
            parcelasEnvio.forEach((id) => fd.append('parcelas[]', id));
            const r = await axios.post(`${API_URL}/public/nf-upload/${token}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (r.data.rejeitada) {
                setResultado({ tipo: 'rejeitada', validacao: r.data.validacao, instrucoes: r.data.instrucoes, mensagem: r.data.mensagem });
            } else if (r.data.sucesso) {
                setResultado({
                    tipo: 'ok', validacao: r.data.validacao || null, canceladas: r.data.canceladas || 0,
                    linkAberto: !!r.data.link_aberto, faltam: r.data.parcelas_pendentes || [],
                });
            } else {
                setErro(r.data.mensagem || 'Não foi possível enviar. Tente de novo.');
            }
            window.scrollTo(0, 0);
        } catch (e) {
            const d = e.response?.data;
            if (d?.codigo && ESTADOS_LINK[d.codigo]) setEstadoLink({ codigo: d.codigo, mensagem: d.mensagem });
            else setErro(d?.mensagem || 'Não foi possível enviar. Verifique sua conexão e tente de novo.');
        } finally {
            setEnviando(false);
        }
    };

    const reenviar = async () => {
        setResultado(null);
        setArquivos([]);
        setErro(null);
        await carregar();
        window.scrollTo(0, 0);
    };

    if (loading) {
        return (
            <div className="fin-pp nfl">
                <Cabecalho />
                <div className="nfl__wrap"><div className="nfl__loading"><span className="nfl__spin" /> Carregando…</div></div>
            </div>
        );
    }

    if (estadoLink) {
        const e = ESTADOS_LINK[estadoLink.codigo] || { titulo: 'Não foi possível abrir o link', texto: estadoLink.mensagem };
        const ok = estadoLink.codigo === 'processado';
        return (
            <div className="fin-pp nfl">
                <Cabecalho />
                <div className="nfl__wrap">
                    <section className="nfl__card nfl__status">
                        <span className={`nfl__status-ic ${ok ? 'is-ok' : 'is-danger'}`}>
                            {ok ? <FiCheckCircle size={30} /> : <FiAlertCircle size={30} />}
                        </span>
                        <h2 className="nfl__status-title">{e.titulo}</h2>
                        <p className="nfl__status-text">{e.texto}</p>
                    </section>
                </div>
            </div>
        );
    }

    const evento = dados?.instrucoes?.evento?.nome || dados?.evento_nome;

    if (resultado?.tipo === 'rejeitada') {
        const v = resultado.validacao || {};
        const nota = v.nota !== false; // D-0122: apólice, recibo… = "documento"
        return (
            <div className="fin-pp nfl">
                <Cabecalho evento={evento} />
                <div className="nfl__wrap">
                    <section className="nfl__card nfl__status">
                        <span className="nfl__status-ic is-danger"><FiAlertTriangle size={30} /></span>
                        <h2 className="nfl__status-title">{nota ? 'Nota não aceita' : 'Documento não aceito'}</h2>
                        <p className="nfl__status-text">{v.mensagem || 'A nota tem dados diferentes do esperado:'}</p>
                        <ListaMotivos itens={v.itens} />
                        <p className="nfl__status-text nfl__muted">Corrija {nota ? 'a nota' : 'o documento'} e envie de novo por este mesmo link. A equipe da Access já foi avisada.</p>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary nfl__cta" onClick={reenviar}>
                            <FiRefreshCw size={18} /> {nota ? 'Enviar nota corrigida' : 'Enviar documento corrigido'}
                        </button>
                    </section>
                    <ObservacoesAccess texto={dados?.observacao_solicitante} />
                    <Instrucoes i={resultado.instrucoes || dados?.instrucoes} fallback={dados} semEvento={!!dados?.orientacoes}
                        parcelas={parcelas} marcadas={marcadas} travado />
                    <Orientacoes o={dados?.orientacoes} />
                </div>
            </div>
        );
    }

    if (resultado?.tipo === 'ok') {
        const v = resultado.validacao;
        const nota = !v || v.nota !== false; // D-0122: apólice, recibo… = "documento"
        return (
            <div className="fin-pp nfl">
                <Cabecalho evento={evento} />
                <div className="nfl__wrap">
                    <section className="nfl__card nfl__status">
                        <span className="nfl__status-ic is-ok"><FiCheckCircle size={30} /></span>
                        <h2 className="nfl__status-title">{nota ? 'Nota recebida' : 'Documento recebido'}</h2>
                        <p className="nfl__status-text">
                            {v && v.status === 'nao_verificado'
                                ? v.mensagem
                                : (nota ? 'Sua nota foi conferida e enviada' : 'Seu documento foi conferido e enviado')
                                    + ' ao Financeiro da Access.' + (resultado.linkAberto ? '' : ' Não é preciso fazer mais nada.')}
                        </p>
                        {resultado.linkAberto ? (
                            <div className="nfl__faltam" data-parcelas-faltam>
                                <p className="nfl__status-text">Ainda falta a nota {resultado.faltam.length > 1 ? 'destas parcelas' : 'desta parcela'}:</p>
                                <ul className="nfl__faltam-lista">
                                    {resultado.faltam.map((p) => (
                                        <li key={p.id}>
                                            <span>{rotuloParcela(p)}{p.vencimento ? <span className="nfl__muted"> · pagamento previsto para {p.vencimento}</span> : null}</span>
                                            <span className="nfl__num">{p.valor_fmt || brl(p.valor)}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="nfl__status-text nfl__muted">Envie quando a nota estiver emitida, por este mesmo link{validadeTexto(dados)}.</p>
                                <button type="button" className="fin-pp__btn fin-pp__btn--primary nfl__cta" onClick={reenviar}>
                                    <FiUploadCloud size={18} /> Enviar a nota de outra parcela
                                </button>
                            </div>
                        ) : null}
                        {resultado.canceladas > 0 ? (
                            <p className="nfl__status-text nfl__muted">
                                {resultado.canceladas === 1
                                    ? 'A nota cancelada também foi recebida e fica como comprovante para o Financeiro.'
                                    : `As ${resultado.canceladas} notas canceladas também foram recebidas e ficam como comprovante para o Financeiro.`}
                            </p>
                        ) : null}
                    </section>
                </div>
            </div>
        );
    }

    const rej = dados?.ultima_rejeicao;
    const validade = soData(dados?.data_expiracao);

    return (
        <div className="fin-pp nfl">
            <Cabecalho evento={evento} />
            <div className="nfl__wrap">
                <Passos />
                <AvisoFechamento orientacoes={dados?.orientacoes} />
                {rej ? (
                    <section className="nfl__alert" role="alert">
                        <div className="nfl__alert-head">
                            <FiAlertTriangle size={18} />
                            <span>A nota enviada{rej.em ? ` em ${dataHora(rej.em)}` : ''} não foi aceita</span>
                        </div>
                        <ListaMotivos itens={rej.itens} />
                        <p className="nfl__alert-foot">Envie a nota corrigida abaixo.</p>
                    </section>
                ) : null}

                <ObservacoesAccess texto={dados?.observacao_solicitante} />
                <Instrucoes i={dados?.instrucoes} fallback={dados} semEvento={!!dados?.orientacoes}
                    parcelas={parcelas} marcadas={marcadas} travado={enviando}
                    onAlternar={(id) => setMarcadas((atual) => alternarMarcada(atual, id))} />
                <Orientacoes o={dados?.orientacoes} />

                <section className="nfl__card">
                    <h2 className="nfl__h2"><span className="nfl__step">3</span> {rej ? 'Envie a nota corrigida' : 'Envie a nota'}</h2>

                    <input
                        ref={inputRef}
                        type="file"
                        accept=".pdf,.xml,text/xml,application/xml,image/jpeg,image/png"
                        onChange={(e) => { escolher(e.target.files[0]); e.target.value = ''; }}
                        hidden
                    />

                    {arquivos.length > 0 ? (
                        <ul className="nfl__files">
                            {arquivos.map((a) => (
                                <li key={a.id} className={`nfl__file ${a.tipo === 'cancelada' ? 'is-cancelada' : 'is-valida'}`}>
                                    <div className="nfl__file-top">
                                        <FiFileText size={22} className="nfl__file-ic" />
                                        <div className="nfl__file-txt">
                                            <span className="nfl__file-name" title={a.file.name}>{nomeCurto(a.file.name)}</span>
                                            <span className="nfl__muted">{tamanho(a.file.size)}</span>
                                        </div>
                                        {!enviando ? (
                                            <button type="button" className="nfl__file-x" onClick={() => remover(a.id)} aria-label="Remover arquivo">
                                                <FiX size={18} />
                                            </button>
                                        ) : null}
                                    </div>
                                    <div className="nfl__seg" role="radiogroup" aria-label="Tipo da nota">
                                        <button type="button" role="radio" aria-checked={a.tipo === 'valida'} disabled={enviando}
                                            className={`nfl__seg-btn ${a.tipo === 'valida' ? 'is-on is-ok' : ''}`} onClick={() => marcar(a.id, 'valida')}>
                                            <FiCheckCircle size={15} /> Nota válida
                                        </button>
                                        <button type="button" role="radio" aria-checked={a.tipo === 'cancelada'} disabled={enviando}
                                            className={`nfl__seg-btn ${a.tipo === 'cancelada' ? 'is-on is-danger' : ''}`} onClick={() => marcar(a.id, 'cancelada')}>
                                            <FiX size={15} /> Nota cancelada
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : null}

                    {arquivos.length < MAX_ARQUIVOS && !enviando ? (
                        <button
                            type="button"
                            className={`nfl__drop ${arquivos.length ? 'nfl__drop--mini' : ''} ${dragOver ? 'is-over' : ''}`}
                            onClick={() => inputRef.current && inputRef.current.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); escolher(e.dataTransfer.files[0]); }}
                        >
                            <FiUploadCloud size={arquivos.length ? 20 : 34} />
                            <span className="nfl__drop-main">{arquivos.length ? 'Adicionar outro arquivo' : 'Toque para escolher o arquivo'}</span>
                            {!arquivos.length ? (
                                <span className="nfl__drop-sub">PDF, XML, JPG ou PNG · até 10 MB. O XML permite conferir com mais precisão.</span>
                            ) : null}
                        </button>
                    ) : null}

                    <p className="nfl__tip">
                        <strong>Cancelou uma nota na prefeitura?</strong> Envie também a nota cancelada e marque como
                        {' '}<em>Nota cancelada</em>. Só a <em>Nota válida</em> é conferida e paga.
                    </p>

                    {erro ? <div className="nfl__err" role="alert"><FiAlertCircle size={16} /> {erro}</div> : null}

                    {enviando ? (
                        <div className="nfl__sending">
                            <span className="nfl__spin" />
                            <span>Enviando e conferindo a nota… pode levar até 1 minuto. Não feche esta página.</span>
                        </div>
                    ) : null}

                    {validade ? (
                        <p className="nfl__validade"><FiClock size={13} /> Link válido até {validade}</p>
                    ) : null}
                </section>
            </div>

            <div className="nfl__bar">
                {avisoEnvio ? <p className="nfl__bar-aviso" role="alert"><FiAlertCircle size={14} /> {avisoEnvio}</p> : null}
                <button
                    type="button"
                    className="fin-pp__btn fin-pp__btn--primary nfl__cta"
                    onClick={enviar}
                    disabled={!podeEnviar}
                >
                    {enviando ? <><span className="nfl__spin nfl__spin--w" /> Conferindo…</> : <><FiUploadCloud size={18} /> Enviar nota fiscal</>}
                </button>
            </div>
        </div>
    );
}
